"use client";

import { motion } from "framer-motion";
import { useRouter } from "next/navigation";
import Link from "next/link";
import { Sparkles, Users, BarChart3, ArrowRight, Zap, CheckCircle2 } from "lucide-react";

export default function MusicAnalysisLandingPage() {
  const router = useRouter();

  return (
    <div className="min-h-screen bg-gradient-to-br from-darkMain to-secondary">
      {/* Hero Section */}
      <section className="relative pt-6 pb-16 sm:pb-20 md:pb-24 lg:pb-28 overflow-hidden">
        {/* 배경 장식 요소 */}
        <div className="absolute inset-0 overflow-hidden pointer-events-none">
          <div className="absolute -top-40 -right-40 w-96 h-96 bg-purple-500/20 rounded-full blur-3xl"></div>
          <div className="absolute -bottom-40 -left-40 w-96 h-96 bg-indigo-500/20 rounded-full blur-3xl"></div>
        </div>

        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="text-center mb-12"
          >
            <h1 className="text-4xl sm:text-5xl md:text-6xl lg:text-7xl font-extrabold text-white mb-6 leading-tight">
              <span className="block mb-2">내 음악을</span>
              <span className="block bg-gradient-to-r from-point via-point2 to-point3 bg-clip-text text-transparent">
                분석하고 성장하세요
              </span>
            </h1>
            <p className="text-lg sm:text-xl md:text-2xl text-white/90 mb-4 max-w-3xl mx-auto font-medium">
              AI가 5초 만에 분석하거나, 전문가가 직접 피드백을 제공합니다
            </p>
          </motion.div>

          {/* 선택 카드 그리드 */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 md:gap-8 max-w-5xl mx-auto mt-16">
            {/* AI 음악 분석 카드 */}
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.2 }}
              className="group relative bg-white rounded-2xl p-8 shadow-xl hover:shadow-2xl transition-all duration-300 border border-gray-200 hover:border-purple-300 cursor-pointer"
              onClick={() => router.push("/music-analysis/analyze")}
            >
              <div className="flex items-center gap-4 mb-6">
                <div className="w-16 h-16 bg-gradient-to-br from-purple-500 to-indigo-500 rounded-xl flex items-center justify-center shadow-lg">
                  <Sparkles className="w-8 h-8 text-white" />
                </div>
                <div>
                  <h2 className="text-2xl font-bold text-gray-900">AI 음악 분석</h2>
                  <p className="text-sm text-gray-500">즉시 결과 확인</p>
                </div>
              </div>

              <p className="text-gray-700 mb-6 leading-relaxed">
                AI가 당신의 음악을 분석하고 객관적인 피드백을 제공합니다. 
                보컬, 연주, 작곡 등 모든 유형을 지원합니다.
              </p>

              <div className="space-y-3 mb-6">
                <div className="flex items-center gap-3">
                  <CheckCircle2 className="w-5 h-5 text-green-500 flex-shrink-0" />
                  <span className="text-sm text-gray-600">5초 만에 분석 완료</span>
                </div>
                <div className="flex items-center gap-3">
                  <CheckCircle2 className="w-5 h-5 text-green-500 flex-shrink-0" />
                  <span className="text-sm text-gray-600">무료로 시작 가능</span>
                </div>
                <div className="flex items-center gap-3">
                  <CheckCircle2 className="w-5 h-5 text-green-500 flex-shrink-0" />
                  <span className="text-sm text-gray-600">상세 분석은 포인트로 이용</span>
                </div>
              </div>

              <button className="w-full bg-gradient-to-r from-purple-600 to-indigo-600 hover:from-purple-700 hover:to-indigo-700 text-white font-semibold py-3 px-6 rounded-xl transition-all duration-300 flex items-center justify-center gap-2 group-hover:gap-3">
                <span>AI 분석 시작하기</span>
                <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
              </button>
            </motion.div>

            {/* 전문가 분석 카드 */}
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.3 }}
              className="group relative bg-white rounded-2xl p-8 shadow-xl hover:shadow-2xl transition-all duration-300 border border-gray-200 hover:border-indigo-300 cursor-pointer"
              onClick={() => router.push("/expert-feedback")}
            >
              <div className="flex items-center gap-4 mb-6">
                <div className="w-16 h-16 bg-gradient-to-br from-indigo-500 to-blue-500 rounded-xl flex items-center justify-center shadow-lg">
                  <Users className="w-8 h-8 text-white" />
                </div>
                <div>
                  <h2 className="text-2xl font-bold text-gray-900">프로 피드백</h2>
                  <p className="text-sm text-gray-500">맞춤형 조언</p>
                </div>
              </div>

              <p className="text-gray-700 mb-6 leading-relaxed">
                현직 프로가 직접 듣고 분석하여 개인 맞춤형 피드백을 제공합니다. 
                더 깊이 있는 조언이 필요할 때 추천합니다.
              </p>

              <div className="space-y-3 mb-6">
                <div className="flex items-center gap-3">
                  <CheckCircle2 className="w-5 h-5 text-blue-500 flex-shrink-0" />
                  <span className="text-sm text-gray-600">48시간 이내 결과 제공</span>
                </div>
                <div className="flex items-center gap-3">
                  <CheckCircle2 className="w-5 h-5 text-blue-500 flex-shrink-0" />
                  <span className="text-sm text-gray-600">현직 프로 1:1 분석</span>
                </div>
                <div className="flex items-center gap-3">
                  <CheckCircle2 className="w-5 h-5 text-blue-500 flex-shrink-0" />
                  <span className="text-sm text-gray-600">맞춤형 개선 방안 제시</span>
                </div>
              </div>

              <button className="w-full bg-gradient-to-r from-indigo-600 to-blue-600 hover:from-indigo-700 hover:to-blue-700 text-white font-semibold py-3 px-6 rounded-xl transition-all duration-300 flex items-center justify-center gap-2 group-hover:gap-3">
                <span>프로 피드백 요청</span>
                <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
              </button>
            </motion.div>
          </div>

          {/* 간단한 비교 섹션 */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.4 }}
            className="mt-16 max-w-4xl mx-auto"
          >
            <div className="bg-white/90 backdrop-blur-base rounded-2xl p-8 shadow-lg border border-white/20">
              <h3 className="text-xl font-bold text-gray-900 mb-6 text-center">
                어떤 분석을 선택해야 할까요?
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="text-center">
                  <Zap className="w-8 h-8 text-purple-600 mx-auto mb-3" />
                  <h4 className="font-semibold text-gray-900 mb-2">빠른 피드백이 필요하다면</h4>
                  <p className="text-sm text-gray-600">
                    AI 음악 분석을 추천합니다. 즉시 결과를 확인할 수 있어요.
                  </p>
                </div>
                <div className="text-center">
                  <BarChart3 className="w-8 h-8 text-indigo-600 mx-auto mb-3" />
                  <h4 className="font-semibold text-gray-900 mb-2">깊이 있는 조언이 필요하다면</h4>
                  <p className="text-sm text-gray-600">
                    프로 피드백을 추천합니다. 맞춤형 솔루션을 받을 수 있어요.
                  </p>
                </div>
              </div>
            </div>
          </motion.div>
        </div>
      </section>
    </div>
  );
}

